import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import CodeBlock from '../CodeBlock/CodeBlock';
import PMCard from './PMCard';
import PMStatus from './PMStatus';
import PMProgress from './PMProgress';
import PMList from './PMList';
import PMTable from './PMTable';
import FilePreview from './FilePreview';
import { CustomUnorderedList, CustomOrderedList } from './CustomList';
import { CustomTable, CustomTableHead, CustomTableBody, CustomTableRow, CustomTableHeader, CustomTableCell } from './CustomTable';
import './PMRenderer.css';
import InternalLink from './InternalLink';

const PMRenderer = ({ content, className = '', isStreaming = false, ...props }) => {
  
  const isS3Url = (url) => {
    if (!url || typeof url !== 'string') return false;
    const s3Patterns = [
      /https:\/\/([^\/]+\.)?s3([^\/]*)?\.amazonaws\.com\//,
      /https:\/\/s3([^\/]*)?\.amazonaws\.com\/[^\/]+\//,
      /https:\/\/[^\/]+\.s3-[^\/]+\.amazonaws\.com\//,
      /https:\/\/[^\/]+\.s3\.[^\/]+\.amazonaws\.com\//
    ];
    return s3Patterns.some(pattern => pattern.test(url));
  };


  const getVisibleTableData = (tableElement) => {
    const rows = tableElement.querySelectorAll('tr');
    const data = [];
    rows.forEach(row => {
      const cells = row.querySelectorAll('th, td');
      const rowData = Array.from(cells).map(cell => cell.textContent.trim());
      data.push(rowData);
    });
    return data;
  };

  const CustomLink = React.useCallback(({ children, href, ...props }) => {
    if (!href) {
      return <InternalLink {...props}>{children}</InternalLink>;
    }

    const textFromChildren = React.Children.toArray(children).map(c => (typeof c === 'string' ? c : '')).join('');
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textFromChildren.trim());
    const isMailto = href.startsWith('mailto:');
    if (looksLikeEmail || isMailto) {
      const { className: passedClassName, ...restProps } = props || {};
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={`text-blue-600 dark:text-blue-400 hover:underline ${passedClassName || ''}`} {...restProps}>
          {children}
        </a>
      );
    }

    const fileExtension = href.split('.').pop()?.toLowerCase() || '';
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(fileExtension);
    const isPdf = fileExtension === 'pdf';

    if (isImage || isPdf) {
      const labelText = React.Children.toArray(children).map(c => (typeof c === 'string' ? c : '')).join('').trim();
      const altText = labelText || href.split('/').pop();
      const isCitationStylePDF = isPdf && /^\[\s*pdf\s*\]/i.test(labelText);
      if (isImage) {
        return <FilePreview src={href} alt={altText} type="image">{children}</FilePreview>;
      }
      if (isPdf && !isCitationStylePDF) {
        return <FilePreview src={href} alt={altText} type="pdf">{children}</FilePreview>;
      }
    }

    let displayHost = '';
    let isGithubCommitOrPR = false;
    try {
      const urlObj = new URL(href);
      displayHost = urlObj.hostname;
      const path = urlObj.pathname || '';
      const pathSegments = path.split('/').filter(Boolean);
      const isGithub = /(^|\.)github\.com$/i.test(displayHost);
      isGithubCommitOrPR = isGithub && (pathSegments.includes('commit') || pathSegments.includes('pull'));
    } catch {}

    if (isGithubCommitOrPR) {
      const { className: passedClassName, ...restProps } = props || {};
      return (
          <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-blue-600 dark:text-blue-400 hover:underline ${passedClassName || ''}`}
              {...restProps}
          >
            {children}
          </a>
      );
    }
    return (
      <InternalLink href={href} {...props}>
        {children}
      </InternalLink>
    );
  }, []);

  // Export table to CSV
  const exportTableToCSV = React.useCallback((tableElement) => {
    try {
      const data = getVisibleTableData(tableElement);
      const csvContent = data.map(row => 
        row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'table_export.csv';
      link.click();
    } catch (error) {
      console.error('Failed to export table:', error);
    }
  }, []);

  // Copy table to clipboard
  const copyTableToClipboard = React.useCallback((tableElement) => {
    try {
      const data = getVisibleTableData(tableElement);
      const textContent = data.map(row => row.join('\t')).join('\n');
      navigator.clipboard.writeText(textContent);
    } catch (error) {
      console.error('Failed to copy table:', error);
    }
  }, []);

  // Custom components for ReactMarkdown with dark mode support
  const customComponents = {
    strong: ({ children, ...props }) => (
      <strong className="dark:text-white text-gray-900 font-black" {...props}>
        {children}
      </strong>
    ),
    b: ({ children, ...props }) => (
      <b className="dark:text-white text-gray-900 font-black" {...props}>
        {children}
      </b>
    ),
    mark: ({ children, ...props }) => (
      <mark className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded text-gray-900 dark:text-gray-100" {...props}>
        {children}
      </mark>
    ),
    span: ({ children, style, ...props }) => {
      // Handle inline color and background-color styles
      const inlineStyle = {};
      if (style?.color) {
        inlineStyle.color = style.color;
      }
      if (style?.backgroundColor) {
        inlineStyle.backgroundColor = style.backgroundColor;
      }
      return (
        <span style={inlineStyle} {...props}>
          {children}
        </span>
      );
    },
    em: ({ children, ...props }) => (
      <em className="dark:text-gray-200 text-gray-600 italic" {...props}>
        {children}
      </em>
    ),
    ul: CustomUnorderedList,
    ol: CustomOrderedList,
    li: ({ children, ...props }) => (
      <li className="dark:text-gray-200 text-[#0e0f11] dark:text-[#e5e7eb] text-[0.875rem] leading-relaxed mb-0" {...props}>
        {children}
      </li>
    ),
    h1: ({ children, ...props }) => (
      <h1 className="dark:text-white text-gray-800 text-2xl font-bold" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 style={{marginTop: '0.4rem', paddingTop: '1rem'}} className="border-t border-gray-200 dark:border-gray-700 dark:text-white text-gray-800 text-xl font-bold" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 style={{marginTop: '0.4rem', paddingTop: '1rem'}} className="dark:text-white border-t border-gray-200 dark:border-gray-700 text-gray-800 text-lg font-bold" {...props}>
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 className="dark:text-white text-gray-800 text-base font-bold" {...props}>
        {children}
      </h4>
    ),
    p: ({ children, ...props }) => (
      <p style={{marginBottom: '0.5rem', paddingBottom: '0'}} className="dark:text-gray-200 text-gray-700" {...props}>
        {children}
      </p>
    ),
    // Disable horizontal rules (---) by rendering nothing
    hr: () => null,
    code: ({ children, className, ...props }) => {
      // For inline code
      if (!className) {
        return (
          <code className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 rounded text-sm font-mono" {...props}>
            {children}
          </code>
        );
      }
      // For code blocks, let CodeBlock component handle it
      return <code className={className} {...props}>{children}</code>;
    },
    // Enhanced image component with preview for S3 URLs
    img: ({ src, alt, ...props }) => {
      if (isS3Url(src)) {
        return <FilePreview src={src} alt={alt} className="my-2" {...props} />;
      }
      // For non-S3 images, use default img tag
      return (
        <img 
          src={src} 
          alt={alt} 
          className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600 my-2"
          {...props} 
        />
      );
    },
    // Custom table components with export functionality
    table: (props) => <CustomTable {...props} exportTableToCSV={exportTableToCSV} copyTableToClipboard={copyTableToClipboard} />,
    thead: CustomTableHead,
    tbody: CustomTableBody,
    tr: CustomTableRow,
    th: CustomTableHeader,
    td: CustomTableCell,
    a: CustomLink
  };

  // Memoized parsing of the content to avoid re-renders
  const parsedContent = React.useMemo(() => {
    if (!content || typeof content !== 'string') {
      return <span>{content}</span>;
    }

    const elements = [];
    let currentIndex = 0;
    const text = content;

    // Only handle card, status, progress PM tags and code blocks now
    const blockStartRegex = /(<pm:(card|status|progress|list|table)(?:\s+[^>]*?)?>)|(```(\w*)?\n)/g;

    while (currentIndex < text.length) {
      blockStartRegex.lastIndex = currentIndex;
      
      const match = blockStartRegex.exec(text);
      if (match) {
        const matchIndex = match.index;
        // 1. Add any plain text before this block
        if (matchIndex > currentIndex) {
          const textBefore = text.substring(currentIndex, matchIndex);
          elements.push(
            <ReactMarkdown
              key={`text-${currentIndex}`}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={customComponents}
            >
              {textBefore}
            </ReactMarkdown>
          );
        }

        const key = `block-${matchIndex}`;
        
        // 2. Handle the block itself
        if (match[1]) { // It's a PM tag
          const tagName = match[2];
          const closingTag = `</pm:${tagName}>`;
          const endTagIndex = text.indexOf(closingTag, matchIndex + match[0].length);
          const blockEndsAt = (endTagIndex !== -1) ? endTagIndex + closingTag.length : text.length;
          
          let blockContent = text.substring(matchIndex + match[0].length, blockEndsAt);
          if (endTagIndex !== -1) {
              blockContent = blockContent.slice(0, -closingTag.length);
          }
          
          // Clean up the content to remove excessive indentation that might cause code block interpretation
          blockContent = blockContent.replace(/^\n+/, '').replace(/\n+$/, '');
          
          // If content has leading whitespace on every line, remove the common indentation
          const lines = blockContent.split('\n');
          if (lines.length > 1) {
            const nonEmptyLines = lines.filter(line => line.trim().length > 0);
            if (nonEmptyLines.length > 0) {
              const minIndent = Math.min(...nonEmptyLines.map(line => {
                const match = line.match(/^(\s*)/);
                return match ? match[1].length : 0;
              }));
              if (minIndent > 0) {
                blockContent = lines.map(line => 
                  line.length > minIndent ? line.substring(minIndent) : line
                ).join('\n');
              }
            }
          }
          
          const attrs = {};
          if (match[0]) {
            const attrRegex = /(\w+)="([^"]*?)"/g;
            let attrMatch;
            while ((attrMatch = attrRegex.exec(match[0])) !== null) {
              attrs[attrMatch[1]] = attrMatch[2];
            }
          }
          
          switch (tagName) {
            case 'card': elements.push(<PMCard key={key} {...attrs}>{blockContent}</PMCard>); break;
            case 'status': elements.push(<PMStatus key={key} {...attrs}>{blockContent}</PMStatus>); break;
            case 'progress': elements.push(<PMProgress key={key} {...attrs} />); break;
            case 'list': elements.push(<PMList key={key} {...attrs}>{blockContent}</PMList>); break;
            case 'table': elements.push(<PMTable key={key} {...attrs}>{blockContent}</PMTable>); break;
            default: elements.push(<span key={key}>{text.substring(matchIndex, blockEndsAt)}</span>);
          }
          currentIndex = blockEndsAt;

        } else if (match[3]) { // It's a code block
          const endTag = '```';
          const endTagIndex = text.indexOf(endTag, matchIndex + match[0].length);
          const blockEndsAt = (endTagIndex !== -1) ? endTagIndex + endTag.length : text.length;

          const blockContent = text.substring(matchIndex, blockEndsAt);
          let codeContent = blockContent.substring(match[0].length);
           if (endTagIndex !== -1) {
               codeContent = codeContent.slice(0, -endTag.length);
           }
          
          elements.push(
            <CodeBlock key={key} className={`language-${match[4] || ''}`} isStreaming={isStreaming}>
              {codeContent}
            </CodeBlock>
          );
          currentIndex = blockEndsAt;
        }

      } else {
        // No more blocks found, add the rest of the text
        const remainingText = text.substring(currentIndex);
        elements.push(
          <ReactMarkdown
            key={`text-${currentIndex}`}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={customComponents}
          >
            {remainingText}
          </ReactMarkdown>
        );
        currentIndex = text.length;
      }
    }

    return elements;
  }, [content, isStreaming, customComponents, exportTableToCSV, copyTableToClipboard]);
  
  return (
    <>
      <div className={`pm-renderer ${className}`} {...props}>
        {parsedContent}
      </div>
    </>
  );
};

export default PMRenderer; 